import { useContext, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import styled from "styled-components";
import { PrimaryPurpleButton, SecondaryGreenButton } from "../../../components/Buttons/styles";
import { ReportDetailsContext, ReportFilters, FinancialReportFilters } from "../../../context/admin/reportDetails/ReportDetailsContext";
import EcommerceManagementServices from "../../../services/EcommerceManagementServices";
import { getInstitutionId } from "../../../util/UserUtil";
import { ReportFilterPayload } from "../../../domain/payloads/progressReport/ReportFilterPayload";
import { FinancialReportFilterPayload } from "../../../domain/payloads/financialReport/FinancialReportFilterPayload";
import { CourseOptionViewModel } from "../../../domain/viewmodels/report/CourseOptionViewModel";
import { ReportDetailEnum } from "../../../domain/enum/ReportDetailEnum";

const ecommerceService = new EcommerceManagementServices();

function buildFinancialFilterSummary(
    filters: FinancialReportFilters,
    courseOptions: CourseOptionViewModel[]
): string {
    const parts: string[] = [];

    if (filters.courseIds.length > 0) {
        const courseNames = filters.courseIds
            .map(id => courseOptions.find(c => c.id === id)?.title ?? id);
        const displayed = courseNames.slice(0, 2).join(', ');
        const extra = courseNames.length > 2 ? ` (+${courseNames.length - 2})` : '';
        parts.push(`Cursos: ${displayed}${extra}`);
    }

    if (filters.studentName) {
        parts.push(`Aluno: ${filters.studentName}`);
    }

    const formatDate = (d: Date | null) =>
        d ? d.toLocaleDateString('pt-BR') : null;

    if (filters.purchaseDateFrom && filters.purchaseDateTo) {
        parts.push(`Período: ${formatDate(filters.purchaseDateFrom)} - ${formatDate(filters.purchaseDateTo)}`);
    } else if (filters.purchaseDateFrom) {
        parts.push(`A partir de: ${formatDate(filters.purchaseDateFrom)}`);
    } else if (filters.purchaseDateTo) {
        parts.push(`Até: ${formatDate(filters.purchaseDateTo)}`);
    }

    return parts.length > 0 ? parts.join(' | ') : "Nenhum filtro aplicado";
}

function buildFilterSummary(
    filters: ReportFilters,
    courseOptions: CourseOptionViewModel[]
): string {
    const parts: string[] = [];

    if (filters.courseIds.length > 0) {
        const courseNames = filters.courseIds
            .map(id => courseOptions.find(c => c.id === id)?.title ?? id);
        const displayed = courseNames.slice(0, 2).join(', ');
        const extra = courseNames.length > 2 ? ` (+${courseNames.length - 2})` : '';
        parts.push(`Cursos: ${displayed}${extra}`);
    }

    if (filters.studentName) {
        parts.push(`Aluno: ${filters.studentName}`);
    }

    const formatDate = (d: Date | null) =>
        d ? d.toLocaleDateString('pt-BR') : null;

    const fromDate = filters.moduleStartDateFrom ?? filters.moduleEndDateFrom;
    const toDate = filters.moduleStartDateTo ?? filters.moduleEndDateTo;
    if (fromDate && toDate) {
        parts.push(`Período: ${formatDate(fromDate)} - ${formatDate(toDate)}`);
    }

    return parts.length > 0 ? parts.join(' | ') : "Nenhum filtro aplicado";
}

interface ExportReportModalProps {
    show: boolean;
    activeTabLabel: string;
    onClose: () => void;
}

export function ExportReportModal({ show, activeTabLabel, onClose }: ExportReportModalProps) {
    const {
        activeTab,
        filters, courseOptions, sortBy, sortDirection,
        indicatorsFilters, indicatorsSortBy, indicatorsSortDirection,
        financialFilters, financialSortBy, financialSortDirection,
    } = useContext(ReportDetailsContext);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const isIndicatorsTab = activeTab === ReportDetailEnum.consumptionDetailed;
    const isFinancialTab = activeTab === ReportDetailEnum.financial;

    const appliedFilters = isFinancialTab
        ? buildFinancialFilterSummary(financialFilters, courseOptions)
        : buildFilterSummary(isIndicatorsTab ? indicatorsFilters : filters, courseOptions);

    const handleExport = async () => {
        const institutionId = getInstitutionId();
        if (!institutionId) return;

        setIsExporting(true);
        setExportError(null);

        try {
            const toIso = (d: Date | null) => d ? d.toISOString().split('T')[0] : undefined;

            if (isFinancialTab) {
                const payload: FinancialReportFilterPayload = {
                    courseIds: financialFilters.courseIds.length > 0 ? financialFilters.courseIds : undefined,
                    studentName: financialFilters.studentName || undefined,
                    purchaseDateFrom: toIso(financialFilters.purchaseDateFrom),
                    purchaseDateTo: toIso(financialFilters.purchaseDateTo),
                    sortBy: financialSortBy || undefined,
                    sortDirection: financialSortBy ? financialSortDirection : undefined,
                };
                await ecommerceService.exportFinancialReport(institutionId, payload);
            } else {
                const activeFilters = isIndicatorsTab ? indicatorsFilters : filters;
                const activeSortBy = isIndicatorsTab ? indicatorsSortBy : sortBy;
                const activeSortDirection = isIndicatorsTab ? indicatorsSortDirection : sortDirection;

                const payload: ReportFilterPayload = {
                    courseIds: activeFilters.courseIds.length > 0 ? activeFilters.courseIds : undefined,
                    studentName: activeFilters.studentName || undefined,
                    moduleStartDateFrom: toIso(activeFilters.moduleStartDateFrom),
                    moduleStartDateTo: toIso(activeFilters.moduleStartDateTo),
                    moduleEndDateFrom: toIso(activeFilters.moduleEndDateFrom),
                    moduleEndDateTo: toIso(activeFilters.moduleEndDateTo),
                    sortBy: activeSortBy || undefined,
                    sortDirection: activeSortBy ? activeSortDirection : undefined,
                };

                if (isIndicatorsTab) {
                    await ecommerceService.exportIndicatorsReport(institutionId, payload);
                } else {
                    await ecommerceService.exportProgressReport(institutionId, payload);
                }
            }
            onClose();
        } catch (err) {
            setExportError("Não foi possível exportar o relatório. Tente novamente.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <ExportReportModalStyles show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Exportar Relatório</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="subtitle">Confirme os detalhes da exportação</p>

                <div className="mb-3">
                    <span className="field-label">Aba selecionada:</span>
                    <div className="info-box mt-2">
                        <span className="tab-value">{activeTabLabel}</span>
                    </div>
                </div>

                <div className="mb-3">
                    <span className="field-label">Filtros aplicados:</span>
                    <div className="info-box mt-2">
                        <span className="filter-value">{appliedFilters}</span>
                    </div>
                </div>

                {exportError && (
                    <p className="export-error">{exportError}</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Row>
                    <Col>
                        <SecondaryGreenButton className="px-4" onClick={onClose} disabled={isExporting}>
                            Cancelar
                        </SecondaryGreenButton>
                    </Col>
                    <Col>
                        <PrimaryPurpleButton className="px-4" onClick={handleExport} disabled={isExporting}>
                            {isExporting ? "Exportando..." : "Exportar"}
                        </PrimaryPurpleButton>
                    </Col>
                </Row>
            </Modal.Footer>
        </ExportReportModalStyles>
    );
}

const ExportReportModalStyles = styled(Modal)`
    .modal-content {
        border-radius: 1.11vmin;
        border: 1px solid #E0E0E0;
        box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
        border-bottom: none;
        padding-bottom: 0;

        .modal-title {
            font-family: Lato;
            font-weight: 700;
            font-size: 1.2rem;
            color: #514F4E;
        }
    }

    .subtitle {
        font-size: 0.85rem;
        color: #7A7A7A;
        margin-bottom: 1rem;
    }

    .field-label {
        font-family: Lato;
        font-weight: 600;
        font-size: 0.9rem;
        color: #514F4E;
    }

    .info-box {
        background-color: #F5F5F5;
        border-radius: 0.375rem;
        padding: 0.75rem 1rem;
    }

    .tab-value {
        color: #0C8292;
        font-weight: 600;
        font-size: 0.9rem;
    }

    .filter-value {
        color: #7A7A7A;
        font-size: 0.9rem;
    }

    .export-error {
        font-size: 0.85rem;
        color: #D30808;
        text-align: center;
    }

    .modal-footer {
        border-top: none;
        justify-content: center;
        gap: 1rem;

        button {
            height: 3rem;
            width: 10rem;
            font-size: 2vmin;
            i {
                font-size: 3.33vmin;
            }
        }
    }
`;
